/* /task/:id and /task/new/:type — the task detail page.
   No mockup exists; `docs/design/point-detail.html` is the closest reference and
   `docs/design/spec/` is normative.

   Shape (spec/03 archetype B): a non-sticky page head, a `minmax(0,720px) 320px` grid of form +
   reference rail, and a bottom save bar that exists only while the form is dirty. The frame is the
   shared `dp-*` chrome in `component/detail-page.scss`.

   No sticky command bar, deliberately — the same call `supervisor/Page.js` made. Archetype B's
   exists so a long form's Save stays in reach; Save is already pinned to the bottom here, so the
   measured-offset apparatus (which has shipped broken four times) buys nothing.

   Things that were broken in the page this replaces, so they do not come back:
     - `PATCH tasks/:id/` returned **405**: `TaskViewSet` had no `UpdateModelMixin`. Saving an
       existing task never worked. picsell-api gained it, plus a narrow `TaskWriteSerializer`.
     - The mode was read from `location.hash`, so `/task/:id/edit` and `/task/:id/view` were dead
       aliases. It rides in the query string as `?edit=1` now, per spec/03.
     - `me` was captured at MODULE LOAD (`getStore().getState()` beside the imports), so a
       language change did not re-render. It is read at render time now.
     - Saving navigated with `window.location.replace` + `document.location.reload()`.
     - `removeTask` read `item.created_by.id` unguarded and threw on a task with no author.
     - Deleting: `DELETE tasks/:id/` hard-deletes a task with no answers and only soft-deletes one
       with answers — one control, two irreversibly different outcomes depending on hidden state.
       Deactivating is `PATCH { is_active: false }`, one verb with one meaning.

   ⚠ Behaviour change needing product sign-off: rejecting an answer no longer creates a duplicate
   follow-up task carrying the rejected photo with the deadline pushed out seven days. It records
   the verdict WITH A REASON (which the old flow never did — it posted `comment: ""`), and the
   deadline is editable on this same page, so re-issuing is an edit rather than a second record.
   `photo-review/RejectTaskModal.js` still does create a corrective task from a rejected photo;
   that flow is unchanged. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Breadcrumb, Button, Empty, Flex, Modal, Skeleton, Space, Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, PictureOutlined, StopOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import RejectTaskModal from "../../component/RejectTaskModal";
import { PersonCell } from "../../component/table-cells";
import { SIDE_NAV, TASK_DETAIL, TASK_LIST } from "../../config/lang";
import { call, getAbortable } from "../../util/action";
import dayjs from "../../util/dayjs";
import { fill, translate } from "../../util/util";
import { photosOf } from "../../util/answer-fields";
import { localeFor } from "../merchandiser/route/dateUtils";
import { StatusTag, postApproval } from "../visit/parts";
import Answers from "./Answers";
import TaskForm, { TARGETS } from "./Form";
import "./tasks.scss";

/** What `PATCH tasks/:id/` accepts — mirrors `TaskWriteSerializer`. Anything outside this is not
    sent, so a partial update cannot blank a field the form does not show (the /profile lesson). */
const WRITABLE = ["title", "description", "start", "end", "images", "template", "is_active"];

const REQUIRED = {
  point: ["title", "end", "template", "point"],
  network: ["title", "end", "template", "network"],
  point_group: ["title", "end", "template", "point_groups"],
};

const MAX_IMAGE_MB = 10;

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

/** Active or deactivated — the only state a task carries now.
 *
 * The four-state completion pill (awaiting / on review / done / rejected) is gone with
 * `annotate_task_status`: it resolved the newest answer and then that answer's `Approval`, a
 * correlated subquery per row over an unindexed `object_id`. The pill's SHAPE is still shared,
 * from `.status-tag` in `theme/antd.scss` (a `-bg` tint with a `-700` label, never antd's
 * `<Tag color="success">`, which derives its label from the `-500` fills and fails AA).
 */
function TaskStatusTag({ isActive, lang }) {
  return (
    <Tag variant="filled" className={`status-tag status-tag--${isActive === false ? "off" : "ok"}`}>
      {translate(TASK_LIST, isActive === false ? "st_inactive" : "st_active", lang)}
    </Tag>
  );
}

/** Two weeks is the window the old page seeded a new task with. */
const seedItem = () => ({
  title: "",
  description: "",
  start: dayjs()
    .startOf("day")
    .toISOString(),
  end: dayjs()
    .add(14, "day")
    .endOf("day")
    .toISOString(),
  images: [],
  template: "",
  point: null,
  network: null,
  point_groups: null,
  title_group_uploads: "",
});

/**
 * The dirty signature.
 *
 * ⚠ NOT a plain `JSON.stringify`: a freshly picked image is a `File`, which serialises to `{}`, so
 * choosing a new photo on an existing record left the form looking pristine and Save disabled.
 * Same replacer `points/ResourcePage.js` carries, for the same bug.
 */
const signature = item =>
  JSON.stringify(item, (key, value) =>
    value instanceof File ? `File:${value.name}:${value.size}:${value.lastModified}` : value instanceof Blob ? `Blob:${value.size}` : value
  );

export default function TaskPage() {
  const history = useHistory();
  const location = useLocation();
  const { id, type } = useParams();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(TASK_DETAIL, key, lang);
  const tl = key => translate(TASK_LIST, key, lang);

  const isNew = !id;
  const target = TARGETS.includes(type) ? type : "point";

  const [item, setItem] = useState(isNew ? seedItem() : null);
  const [status, setStatus] = useState(isNew ? "ready" : "loading");
  /* Two baselines, deliberately. `pristine` is the signature for the dirty comparison; `original`
     is a structuredClone for reverting — `JSON.parse(JSON.stringify(item))` would turn Date fields
     into strings, which threw inside a render on the points page. */
  const [pristine, setPristine] = useState(() => signature(isNew ? seedItem() : {}));
  const original = useRef(null);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  /* The answers section: `undefined` = never opened, `null` = the fetch failed, array = loaded and
     cached. Opening is what triggers the fetch, and it opens on arrival for an existing task — the
     verdict is what a reviewer came here to give, and it used to sit collapsed at the bottom behind
     an extra click (PICSELL-1572). A NEW task has no answers, so it stays shut and fetches nothing.
     The lazy-mount contract is unchanged: the section is still mounted only while open, and still
     unmounted (not `display: none`) when it is closed. */
  const [answersOpen, setAnswersOpen] = useState(!isNew);
  const [answers, setAnswers] = useState(undefined);
  const loadingAnswers = useRef(false);

  /* Edit mode rides in the URL so a reload lands back where the user was (spec/03). */
  const params = new URLSearchParams(location.search);
  const editing = isNew || params.get("edit") === "1";

  const dateFmt = useMemo(() => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "short", year: "numeric" }), [lang]);
  const dateTimeFmt = useMemo(
    () => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
    [lang]
  );

  const setBaseline = next => {
    original.current = structuredClone(next);
    setPristine(signature(next));
  };

  /* ------------------------------------------------------------------ load */

  const load = () => {
    const controller = new AbortController();
    setStatus("loading");
    getAbortable(`tasks/${id}/`, controller.signal)
      .then(response => {
        if (!response) {
          setStatus("error");
          return;
        }
        setItem(response);
        setBaseline(response);
        setStatus("ready");
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setStatus("error");
      });
    return () => controller.abort();
  };

  useEffect(() => {
    if (isNew) {
      setBaseline(item);
      return undefined;
    }
    /* A different record invalidates the cached answers as well as the item. Reopened, not closed —
       closing here would silently undo the open-on-arrival default on every task→task navigation. */
    setAnswers(undefined);
    setAnswersOpen(true);
    return load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* Hydrate the answers on first open, then cache. */
  useEffect(() => {
    if (!answersOpen || isNew || answers !== undefined || loadingAnswers.current) return;
    loadingAnswers.current = true;
    const controller = new AbortController();
    /* `?task=` only started narrowing anything when `AnswerViewSet.filter_class` was corrected to
       `filterset_class` — before that this endpoint returned every answer in the company, and the
       old page rendered all of them as if they belonged to the open task. */
    /* `ordering=-created` because the head's verdict acts on the NEWEST answer and the endpoint
       sends no default ordering of its own — "newest" would otherwise be whatever the database
       happened to return first. */
    getAbortable(`answers/?task=${id}&page_size=100&ordering=-created`, controller.signal)
      .then(response => {
        loadingAnswers.current = false;
        setAnswers(response?.results || []);
      })
      .catch(error => {
        loadingAnswers.current = false;
        if (error.name === "AbortError") return;
        setAnswers(null);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answersOpen, id, answers]);

  /* After moderating, the answers and the task's own status refetch — the status is derived from
     the newest answer's verdict, so it moves when a verdict lands. */
  const afterModerate = () => {
    setAnswers(undefined);
    loadingAnswers.current = false;
    if (!isNew) load();
  };

  /* ------------------------------------------------------- the head's verdict */

  /* ⚠ A verdict is given on an ANSWER, never on the task. `approvals/` would happily accept
     `object_type: "task"` and answer 201 — the model is generic and its serializer validates only
     that a ContentType by that name exists — but `annotate_task_status()` hard-codes
     `content_type__model="answer"` and casts from the Answer pk, so a task-level row is invisible to
     the status pill, the list column, the sort and every segment count. The buttons would appear to
     do nothing (PICSELL-1574 #19).
     THE NEWEST answer — `ordering=-created` above is what makes `[0]` the newest.
     The verdict is `is_supervised` — the plain column ApprovalViewSet mirrors every verdict into
     (PICSELL-1581 #20); the per-row `approval` subqueries it replaced stay deleted. `== null`
     (loose) covers both a genuine NULL and a pre-deploy payload without the field — the latter
     keeps today's offer-on-every-row behaviour rather than hiding the buttons. */
  const newestAnswer = Array.isArray(answers) && answers.length ? answers[0] : null;
  const pendingAnswer = newestAnswer && newestAnswer.is_supervised == null ? newestAnswer : null;
  /* The completion pill: the verdict on the newest answer decides. No answers → no pill; there
     is nothing to review yet and "pending" would claim otherwise. */
  const verdictStatus = newestAnswer
    ? newestAnswer.is_supervised === true
      ? "approved"
      : newestAnswer.is_supervised === false
      ? "rejected"
      : "pending"
    : null;
  const [verdictBusy, setVerdictBusy] = useState(false);
  const [rejectingAnswer, setRejectingAnswer] = useState(null);

  const approveNewest = () => {
    if (!pendingAnswer) return;
    setVerdictBusy(true);
    postApproval({ objectId: pendingAnswer.id, objectType: "answer", isApproved: true, comment: "" }, ok => {
      setVerdictBusy(false);
      if (!ok) {
        Alert.error(t("load_error"), { position: "top-right" });
        return;
      }
      Alert.success(t("approved_msg"), { position: "top-right" });
      afterModerate();
    });
  };

  /* ----------------------------------------------------------------- dirty */

  const dirty = item !== null && signature(item) !== pristine;
  /* A pristine NEW record must still be savable, or Save is dead on arrival and the author cannot
     see what is missing. This shipped broken twice elsewhere. */
  const canSave = isNew ? true : dirty;
  const showSavebar = editing && (isNew || dirty);

  const change = (name, value) => {
    setItem(prev => ({ ...prev, [name]: value }));
    setErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  /* ----------------------------------------------------------------- image */

  const uploadImage = file => {
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      Alert.warning(fill(t("img_too_big"), { n: MAX_IMAGE_MB }), { position: "top-right" });
      return;
    }
    const reader = new FileReader();
    reader.onload = event => {
      /* `images/` takes a data URL (its `Base64ImageField`), not a multipart file — which is why
         antd's own uploader is bypassed with `beforeUpload → false`. `owner` is the id: the old
         code sent the whole `me` object from one call site and `undefined` from another. */
      call({ method: "POST", url: "images/", post: { owner: me?.id, image: event.target.result } }, response => {
        if (!response?.id) {
          Alert.error(t("img_failed"), { position: "top-right" });
          return;
        }
        change("images", [response]);
      });
    };
    reader.readAsDataURL(file);
  };

  /* The reference is dropped from the task; the Image row itself is left alone. Deleting it would
     break any other task pointing at the same upload, and Django would not reap the file anyway. */
  const removeImage = () => change("images", []);

  /* ------------------------------------------------------------------ save */

  const missing = () => {
    const names = REQUIRED[isNew ? target : "point"];
    const out = {};
    names.forEach(name => {
      if (name === "point" && !isNew) return;
      const value = item?.[name];
      if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) out[name] = t("required_field");
    });
    if (isNew && target === "point_group" && !item?.title_group_uploads?.trim()) out.group_name = t("required_field");
    return out;
  };

  /* Focus the FIRST INVALID FIELD, not a button that may no longer exist after the re-render. */
  const focusField = name => {
    const host = document.querySelector(`[data-field="${name}"]`);
    if (!host) return;
    host.scrollIntoView({ behavior: "smooth", block: "center" });
    const control = host.querySelector("input, textarea, .ant-select-selector");
    if (control) control.focus();
  };

  const payload = () => {
    const out = {};
    WRITABLE.forEach(name => {
      if (item[name] === undefined) return;
      out[name] = name === "images" ? (item.images || []).map(idOf).filter(Boolean) : name === "template" ? idOf(item.template) : item[name];
    });
    return out;
  };

  const save = () => {
    const gaps = missing();
    if (Object.keys(gaps).length) {
      setErrors(gaps);
      focusField(Object.keys(gaps)[0]);
      return;
    }
    setErrors({});
    setSaving(true);

    if (!isNew) {
      call({ method: "PATCH", url: `tasks/${item.id}/`, post: payload() }, response => {
        setSaving(false);
        if (!response?.id) {
          Alert.error(t("save_failed"), { position: "top-right" });
          return;
        }
        setItem(response);
        setBaseline(response);
        Alert.success(t("saved_msg"), { position: "top-right" });
        history.replace({ search: "" });
      });
      return;
    }

    /* Create. A point lands on the new record; a network or a group is a FAN-OUT that returns no
       single id — `tasks/` with a `network` creates one task per point in it, and
       `tasks/bulk-create-by-group/` queues a Celery job answering `{action, status}`. Pretending
       either produced one record is what made the old page reload the whole app. */
    if (target === "point_group") {
      const body = {
        action_name: item.title_group_uploads || "",
        group_id: idOf(item.point_groups),
        template: idOf(item.template),
        tasks: [
          {
            title: item.title,
            description: item.description || "",
            start: item.start,
            end: item.end,
            images: (item.images || []).map(idOf).filter(Boolean),
          },
        ],
      };
      call({ method: "POST", url: "tasks/bulk-create-by-group/", post: body }, response => {
        setSaving(false);
        if (!response?.action) {
          Alert.error(t("save_failed"), { position: "top-right" });
          return;
        }
        Alert.success(t("bulk_queued_msg"), { position: "top-right" });
        history.push("/task");
      });
      return;
    }

    const body = {
      ...payload(),
      point: target === "point" ? idOf(item.point) : null,
      network: target === "network" ? idOf(item.network) : null,
      created_by: me?.id || null,
      is_active: true,
    };
    call({ method: "POST", url: "tasks/", post: body }, response => {
      setSaving(false);
      /* A network fan-out answers with a LIST, one task per point; a single point answers with one
         object. Both are successes and they are told apart here rather than assumed. */
      if (Array.isArray(response)) {
        Alert.success(fill(t("bulk_created_msg"), { n: response.length }), { position: "top-right" });
        history.push("/task");
        return;
      }
      if (!response?.id) {
        Alert.error(t("save_failed"), { position: "top-right" });
        return;
      }
      Alert.success(t("saved_msg"), { position: "top-right" });
      history.push(`/task/${response.id}`);
    });
  };

  const cancel = () => {
    if (isNew) {
      history.push("/task");
      return;
    }
    if (!dirty) {
      history.replace({ search: "" });
      return;
    }
    Modal.confirm({
      title: t("confirm_cancel"),
      okText: t("cancel"),
      cancelText: tl("cancel_btn"),
      onOk: () => {
        /* Revert from the structuredClone, never from a re-parsed JSON baseline. */
        const restored = structuredClone(original.current);
        setItem(restored);
        setPristine(signature(restored));
        setErrors({});
        history.replace({ search: "" });
      },
    });
  };

  const deactivate = () => {
    Modal.confirm({
      title: fill(tl("confirm_deactivate"), { n: 1 }),
      content: tl("confirm_deactivate_desc"),
      okText: tl("deactivate"),
      okButtonProps: { danger: true },
      cancelText: tl("cancel_btn"),
      onOk: () => {
        setBusy(true);
        call({ method: "PATCH", url: `tasks/${item.id}/`, post: { is_active: false } }, response => {
          setBusy(false);
          if (!response?.id) {
            Alert.error(t("save_failed"), { position: "top-right" });
            return;
          }
          setItem(response);
          setBaseline(response);
          Alert.success(fill(tl("deactivated_msg"), { n: 1 }), { position: "top-right" });
        });
      },
    });
  };

  /* ----------------------------------------------------------------- states */

  if (status === "loading") {
    return (
      <div className="shell-content">
        <Skeleton active title paragraph={{ rows: 2 }} />
        <div className="dp-grid">
          <Skeleton active paragraph={{ rows: 8 }} />
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      </div>
    );
  }

  if (status === "error" || !item) {
    return (
      <div className="shell-content">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={8} align="center">
              <Typography.Text strong>{t("not_found_title")}</Typography.Text>
              <Typography.Text type="secondary">{t("not_found_desc")}</Typography.Text>
              <Button type="primary" onClick={() => history.push("/task")}>
                {t("back_to_list")}
              </Button>
            </Flex>
          }
        />
      </div>
    );
  }

  const off = item.is_active === false;
  const imageUrl = item.images?.[0]?.image || null;
  /* The list's `?…` ride-along, so Back from here returns to the exact filtered view. */
  const backSearch = location.search.replace(/[?&]edit=1/, "").replace(/^&/, "?");

  const facts = [
    [t("k_created"), item.created ? dateTimeFmt.format(new Date(item.created)) : "—"],
    [t("f_start"), item.start ? dateFmt.format(new Date(item.start)) : "—"],
    [t("f_end"), item.end ? dateFmt.format(new Date(item.end)) : "—"],
    [t("k_locality"), item.point?.locality?.name || "—"],
    [t("f_network"), item.point?.network?.name || "—"],
    [t("k_last_answer"), item.last_answer_date ? dateFmt.format(new Date(item.last_answer_date)) : "—"],
  ];

  return (
    <div className="shell-content dp-detail tk-detail">
      <div className="dp-head">
        <div className="dp-head__main">
          <Breadcrumb
            items={[
              { title: <a onClick={() => history.push(`/task${backSearch}`)}>{translate(SIDE_NAV, "tasks", lang)}</a> },
              { title: isNew ? t("new_title") : item.title || `#${item.id}` },
            ]}
          />
          <div className="dp-head__titlerow">
            <h1 className="dp-title">{isNew ? t("new_title") : item.title || `#${item.id}`}</h1>
            {isNew ? null : <TaskStatusTag isActive={item.is_active} lang={lang} />}
            {/* The verdict on the newest answer, so Approve/Reject visibly changes the head —
                the refetch in `afterModerate` is what moves it (PICSELL-1581 #20). */}
            {verdictStatus ? <StatusTag status={verdictStatus} lang={lang} /> : null}
            {off ? <Tag className="dp-tag--off">{tl("st_inactive")}</Tag> : null}
          </div>
        </div>
        <Space size={8} wrap>
          {/* Offered only while the newest answer is undecided — otherwise the head would invite a
              verdict on a task nobody has answered, or re-decide one already settled. Plain antd
              `default` + `danger`: the green `VerdictButtons` in `visit/sections.js` live in
              `visit-detail.scss`, which is not globally imported, so reaching for them would drag
              another page's stylesheet in for two colours. */}
          {pendingAnswer ? (
            <>
              <Button icon={<CheckCircleOutlined />} loading={verdictBusy} onClick={approveNewest}>
                {t("approve")}
              </Button>
              <Button danger icon={<CloseCircleOutlined />} disabled={verdictBusy} onClick={() => setRejectingAnswer(pendingAnswer)}>
                {t("reject")}
              </Button>
            </>
          ) : null}
          {!isNew && !editing ? (
            <Button icon={<EditOutlined />} onClick={() => history.replace({ search: "?edit=1" })}>
              {t("edit")}
            </Button>
          ) : null}
          {!isNew && !off ? (
            <Button danger icon={<StopOutlined />} loading={busy} onClick={deactivate}>
              {tl("deactivate")}
            </Button>
          ) : null}
        </Space>
      </div>

      <div className="dp-grid">
        <div className="dp-main">
          <TaskForm
            item={item}
            target={target}
            isNew={isNew}
            readOnly={!editing}
            errors={errors}
            lang={lang}
            onChange={change}
            onImage={uploadImage}
            onImageRemove={removeImage}
          />
        </div>

        <aside className="dp-rail">
          {isNew ? null : (
            <section className="dp-card tk-idcard">
              {imageUrl ? (
                <img className="tk-idcard__shot" src={imageUrl} alt="" />
              ) : (
                <div className="tk-idcard__shot tk-shot--empty">
                  <PictureOutlined />
                </div>
              )}
              <span className="tk-idcard__name">{item.title || `#${item.id}`}</span>
              <Typography.Text type="secondary">{item.template?.name || item.point?.name || ""}</Typography.Text>
            </section>
          )}

          <section className="dp-card">
            <div className="dp-sec-head">
              <h2 className="dp-sec-title">{t("rail_summary")}</h2>
            </div>
            <ul className="tk-facts">
              {facts.map(([label, value]) => (
                <li key={label}>
                  <span className="tk-facts__k">{label}</span>
                  <span className="tk-facts__v">{value}</span>
                </li>
              ))}
              <li>
                <span className="tk-facts__k">{t("k_merchandiser")}</span>
                <span className="tk-facts__v">
                  {item.point?.responsible_merchandiser ? (
                    <PersonCell user={item.point.responsible_merchandiser} strong={false} />
                  ) : (
                    <Typography.Text type="secondary">{t("no_merchandiser")}</Typography.Text>
                  )}
                </span>
              </li>
              <li>
                <span className="tk-facts__k">{t("k_created_by")}</span>
                <span className="tk-facts__v">
                  <PersonCell user={item.created_by} strong={false} />
                </span>
              </li>
            </ul>
          </section>

          {/* What is still missing, each row focusing its own field rather than only naming it. */}
          {editing && Object.keys(missing()).length ? (
            <section className="dp-card">
              <div className="dp-sec-head">
                <h2 className="dp-sec-title">{t("rail_quality")}</h2>
              </div>
              <Flex vertical gap={4} align="flex-start">
                {Object.keys(missing()).map(name => (
                  <Button key={name} type="link" className="tk-issue" onClick={() => focusField(name)}>
                    {t(`f_${name}`) || name}
                  </Button>
                ))}
              </Flex>
            </section>
          ) : null}
        </aside>
      </div>

      {/* The answers section sits BELOW the two-column grid, not inside it.
          It is a seven-column table, so it wants the full width — but spanning `1 / -1` *inside*
          the grid put it in row 2 while the sticky rail occupied a taller row 1, and the rail then
          hung 320×181px over it (measured, not guessed). Outside the grid there is no track for the
          rail to overflow out of, and the table still gets the whole content width.

          The body is mounted only while open; the header carries no count, because the endpoint
          no longer annotates one (`annotate_task_counts` was four DISTINCT counts per row, and
          Django 3.1 keeps them inside the pagination COUNT — see CLAUDE.md). */}
      {isNew ? null : (
        <section className="dp-card tk-answers-card">
          <div className="dp-sec-head">
            <Flex align="center" justify="space-between" gap="small" wrap>
              <h2 className="dp-sec-title">{t("sec_answers")}</h2>
              <Button size="small" aria-expanded={answersOpen} aria-controls="tk-answers-panel" onClick={() => setAnswersOpen(open => !open)}>
                {t(answersOpen ? "ans_hide" : "ans_show")}
              </Button>
            </Flex>
          </div>
          <div id="tk-answers-panel" role="region" aria-label={t("sec_answers")}>
            {answersOpen ? <Answers task={item} rows={answers} lang={lang} dateFmt={dateTimeFmt} onModerated={afterModerate} /> : null}
          </div>
        </section>
      )}

      {showSavebar ? (
        <div className="dp-savebar">
          <span className="dp-savebar__msg">
            {isNew ? <PictureOutlined /> : <EditOutlined />}
            {t(isNew ? "draft" : "unsaved")}
          </span>
          <span className="dp-savebar__acts">
            <Button type="text" onClick={cancel}>
              {t("cancel")}
            </Button>
            <Button type="primary" icon={<CheckCircleOutlined />} loading={saving} disabled={!canSave} onClick={save}>
              {t("save")}
            </Button>
          </span>
        </div>
      ) : null}

      {/* Rejecting records a verdict AND asks the merchandiser to fix the shelf — the same shared
          modal `/photo-review`, `/answers` and `/visit` use. It posts the `approvals/` row itself,
          after creating the corrective task, so a failed task never leaves a rejection with nothing
          attached; that ordering is the reason this flow is shared rather than reimplemented. */}
      {rejectingAnswer ? (
        <RejectTaskModal
          target={{ objectId: rejectingAnswer.id, objectType: "answer" }}
          photo={photosOf(rejectingAnswer)[0] || null}
          point={item?.point || null}
          visitId={rejectingAnswer.visit_id}
          lang={lang}
          onCancel={() => setRejectingAnswer(null)}
          onDone={() => {
            setRejectingAnswer(null);
            afterModerate();
          }}
        />
      ) : null}
    </div>
  );
}
